import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { ScanLine, Camera, Loader2, Plus, Trash2, Minus, ClipboardList, Video, X, Zap, AlertTriangle, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const CONDS = ["NM", "LP", "MP", "HP", "DMG"];
const HOLD_MS = 1200;      // how long the card must stay steady before auto-scan
const MOTION_THRESH = 9;   // mean per-pixel brightness diff that counts as movement
const SAMPLE_W = 32, SAMPLE_H = 24;

const toDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

export default function Scan() {
  const fileRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [recognized, setRecognized] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [queue, setQueue] = useState([]);
  const [pushing, setPushing] = useState(false);
  const [batch, setBatch] = useState(false);
  const [auto, setAuto] = useState(true);
  const [steady, setSteady] = useState(false);
  const [camError, setCamError] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const autoRef = useRef(true);
  const scanningRef = useRef(false);
  const prevSampleRef = useRef(null);
  const stableSinceRef = useRef(null);
  const armedRef = useRef(true);
  const loopRef = useRef(null);
  const sampleCanvasRef = useRef(null);

  useEffect(() => { scanningRef.current = scanning; }, [scanning]);
  useEffect(() => { autoRef.current = auto; }, [auto]);

  const recognizeBase64 = async (b64, isAuto = false) => {
    setScanning(true);
    setRecognized(null);
    setCandidates([]);
    try {
      const { data } = await api.post("/scan/recognize", { image_base64: b64 });
      const cand = data.candidates || [];
      if (isAuto && data.confident && cand.length) {
        addToQueue(cand[0], true);
        return;
      }
      setRecognized(data.recognized || {});
      setCandidates(cand);
      if (!cand.length) toast.error("No match — hold the card straight and fill the frame");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setScanning(false);
    }
  };

  const onCapture = async (file) => {
    if (!file) return;
    const b64 = await toDataUrl(file);
    await recognizeBase64(b64);
    if (fileRef.current) fileRef.current.value = "";
  };

  const sampleMotion = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    if (!sampleCanvasRef.current) {
      const c = document.createElement("canvas");
      c.width = SAMPLE_W; c.height = SAMPLE_H;
      sampleCanvasRef.current = c;
    }
    const ctx = sampleCanvasRef.current.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(v, 0, 0, SAMPLE_W, SAMPLE_H);
    const { data } = ctx.getImageData(0, 0, SAMPLE_W, SAMPLE_H);
    const cur = new Uint8ClampedArray(SAMPLE_W * SAMPLE_H);
    for (let i = 0, j = 0; i < data.length; i += 4, j++) {
      cur[j] = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) | 0;
    }
    const prev = prevSampleRef.current;
    prevSampleRef.current = cur;
    if (!prev) return;
    let sum = 0;
    for (let k = 0; k < cur.length; k++) sum += Math.abs(cur[k] - prev[k]);
    const diff = sum / cur.length;

    if (diff > MOTION_THRESH) {
      stableSinceRef.current = null;
      armedRef.current = true;        // movement seen → ready to scan the next card
      setSteady(false);
      return;
    }
    if (!autoRef.current || !armedRef.current || scanningRef.current) return;
    if (stableSinceRef.current === null) { stableSinceRef.current = Date.now(); return; }
    setSteady(true);
    if (Date.now() - stableSinceRef.current >= HOLD_MS) {
      armedRef.current = false;
      stableSinceRef.current = null;
      setSteady(false);
      captureFrame(true);
    }
  };

  const startBatch = async () => {
    setCamError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      const msg = !window.isSecureContext
        ? "The browser blocks the camera because this page isn't served over HTTPS."
        : "This browser doesn't support live camera access — use 'Scan one card' instead.";
      setCamError(msg);
      toast.error(msg);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
      streamRef.current = stream;
      setBatch(true);
      prevSampleRef.current = null;
      stableSinceRef.current = null;
      armedRef.current = true;
      setTimeout(() => {
        const v = videoRef.current;
        if (v) { v.srcObject = stream; v.play?.().catch(() => {}); }
      }, 0);
      if (loopRef.current) clearInterval(loopRef.current);
      loopRef.current = setInterval(sampleMotion, 200);
    } catch (e) {
      const inIframe = window.self !== window.top;
      const name = e?.name || "";
      let msg;
      if (name === "NotAllowedError" || name === "SecurityError") {
        msg = inIframe
          ? "The camera is blocked inside this embedded preview window. Open the app in its own browser tab (button below), then allow camera access."
          : "Camera permission was denied. Allow camera for this site in your browser settings (address-bar camera icon) and try again.";
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        msg = "No camera was found on this device — use 'Scan one card' to upload a photo instead.";
      } else if (name === "NotReadableError") {
        msg = "The camera is being used by another app. Close it and try again.";
      } else {
        msg = `Camera couldn't start (${name || "unknown error"}). Use 'Scan one card' instead.`;
      }
      setCamError(msg);
      toast.error(msg);
    }
  };

  const stopBatch = () => {
    if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; }
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setBatch(false);
    setSteady(false);
    setRecognized(null);
    setCandidates([]);
  };

  const captureFrame = async (isAuto = false) => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) { if (!isAuto) toast.error("Camera still starting…"); return; }
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    await recognizeBase64(canvas.toDataURL("image/jpeg", 0.85), isAuto);
  };

  useEffect(() => () => {
    if (loopRef.current) clearInterval(loopRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  const addToQueue = (c, autoAdded = false) => {
    setQueue((q) => {
      const key = `${c.tcg_product_id}|NM`;
      const i = q.findIndex((x) => x.key === key);
      if (i >= 0) {
        const n = [...q];
        n[i] = { ...n[i], quantity: n[i].quantity + 1 };
        return n;
      }
      return [...q, { key, ...c, condition: "NM", quantity: 1 }];
    });
    toast.success(`${c.name} ${autoAdded ? "auto-added" : "added"} to scan queue`);
    setRecognized(null);
    setCandidates([]);
  };

  const updateQ = (key, patch) => setQueue((q) => q.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  const removeQ = (key) => setQueue((q) => q.filter((x) => x.key !== key));

  const pushToStaging = async () => {
    if (!queue.length) return;
    setPushing(true);
    try {
      const items = queue.map((q) => ({
        name: q.name, set_name: q.set_name, card_number: q.card_number, category: q.category,
        tcg_product_id: q.tcg_product_id, image_url: q.image_url, item_type: "raw",
        condition: q.condition, quantity: q.quantity,
        tcg_price: q.tcg_price, shopify_price: q.tcg_price,
      }));
      await api.post("/staging/bulk", { items });
      toast.success(`Added ${items.length} card(s) to Staging`);
      setQueue([]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPushing(false);
    }
  };

  const queueTotal = queue.reduce((a, b) => a + (b.tcg_price || 0) * b.quantity, 0);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100">Scan</h1>
        <p className="text-sm text-slate-400 mt-1">Snap a photo of a card on your phone — we recognize it, match the TCGPlayer catalog, and queue it for Staging.</p>
      </div>

      <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden
        data-testid="scan-file-input" onChange={(e) => onCapture(e.target.files?.[0])} />

      {!batch ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button onClick={() => fileRef.current?.click()} disabled={scanning} data-testid="scan-capture-button"
              className="flex items-center justify-center gap-2 py-4 rounded-2xl bg-amber-500 text-[#1a1205] font-bold text-base hover:bg-amber-400 disabled:opacity-60">
              {scanning ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
              {scanning ? "Recognizing…" : "Scan one card"}
            </button>
            <button onClick={startBatch} data-testid="scan-batch-start-button"
              className="flex items-center justify-center gap-2 py-4 rounded-2xl border text-base font-bold text-slate-100 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>
              <Video className="h-5 w-5 text-cyan-400" /> Batch scan
            </button>
          </div>
          {camError && (
            <div className="mt-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4" data-testid="scan-cam-error">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-amber-100 font-semibold mb-1">Live camera couldn't start</div>
                  <p className="text-sm text-amber-100/80">{camError}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <a href={window.location.href} target="_blank" rel="noopener noreferrer" data-testid="scan-open-new-tab"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400">
                      <ExternalLink className="h-4 w-4" /> Open in a new tab
                    </a>
                    <button onClick={() => fileRef.current?.click()} data-testid="scan-cam-error-fallback"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-amber-500/40 text-amber-100 font-semibold text-sm hover:bg-amber-500/10">
                      <Camera className="h-4 w-4" /> Scan one card instead
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="rounded-2xl border overflow-hidden" style={{ background: "#000", borderColor: "var(--pk-border)" }} data-testid="scan-batch-panel">
          <div className="relative">
            <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-[52vh] object-contain bg-black" />
            {auto && (
              <div className={`absolute top-2 left-2 px-2.5 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1.5 transition-colors ${scanning ? "bg-amber-500 text-[#1a1205]" : steady ? "bg-cyan-500 text-[#04121a]" : "bg-black/60 text-slate-200"}`}
                data-testid="scan-auto-status">
                {scanning ? <><Loader2 className="h-3 w-3 animate-spin" /> Reading…</>
                  : steady ? <><Zap className="h-3 w-3" /> Hold steady…</>
                  : <><Zap className="h-3 w-3" /> Point at a card</>}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 p-3" style={{ background: "var(--pk-surface)" }}>
            <button onClick={() => setAuto((a) => !a)} data-testid="scan-auto-toggle"
              className={`flex items-center justify-center gap-2 px-3 py-3 rounded-xl border font-semibold text-sm transition-colors ${auto ? "border-cyan-500/60 bg-cyan-500/15 text-cyan-300" : "text-slate-300"}`}
              style={auto ? {} : { borderColor: "var(--pk-border)" }}>
              <Zap className="h-4 w-4" /> Auto {auto ? "on" : "off"}
            </button>
            <button onClick={() => captureFrame(false)} disabled={scanning} data-testid="scan-batch-capture-button"
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-500 text-[#1a1205] font-bold hover:bg-amber-400 disabled:opacity-60">
              {scanning ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />} Capture
            </button>
            <button onClick={stopBatch} data-testid="scan-batch-stop-button"
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-slate-200 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>
              <X className="h-5 w-5" /> Done
            </button>
          </div>
          <p className="text-[11px] text-slate-500 px-3 pb-3" style={{ background: "var(--pk-surface)" }}>
            {auto
              ? "Auto-capture on: hold a card steady in frame and it scans hands-free. Confident matches are added automatically; if we're unsure, tap the right one below."
              : "Point at a card, tap Capture, then tap the correct match below. The camera stays on for the next card."}
          </p>
        </div>
      )}

      {recognized && (
        <div className="mt-5 rounded-2xl border p-4" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }} data-testid="scan-results">
          <div className="text-xs text-slate-500 mb-3">
            Recognized: <span className="text-slate-200 font-medium">{recognized.name || "—"}</span>
            {recognized.number ? <span className="text-slate-400"> · #{recognized.number}</span> : null}
            {recognized.language === "jp" ? <span className="ml-1 px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 text-[10px]">JP</span> : null}
          </div>
          <div className="space-y-2">
            {candidates.map((c) => (
              <button key={c.tcg_product_id} onClick={() => addToQueue(c)} data-testid="scan-candidate"
                className="w-full flex items-center gap-3 p-2 rounded-xl border text-left hover:border-amber-500/50 hover:bg-[var(--pk-hover)] transition-colors" style={{ borderColor: "var(--pk-border)" }}>
                {c.image_url ? <img src={c.image_url} alt="" className="h-14 w-10 object-contain rounded bg-white/5 shrink-0" /> : <div className="h-14 w-10 rounded bg-white/5 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-100 truncate">{c.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{c.set_name} · #{c.card_number} · {c.rarity}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono-num text-amber-400 text-sm">{fmt(c.tcg_price)}</div>
                  <div className="inline-flex items-center gap-1 text-[11px] text-emerald-400"><Plus className="h-3 w-3" /> Add</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Scan queue */}
      <div className="mt-6 rounded-2xl border overflow-hidden" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--pk-border)" }}>
          <div className="flex items-center gap-2">
            <ScanLine className="h-4 w-4 text-cyan-400" />
            <h2 className="font-heading font-bold text-slate-100">Scan queue ({queue.length})</h2>
          </div>
          <span className="text-xs text-slate-500">est. {fmt(queueTotal)}</span>
        </div>

        {queue.length === 0 ? (
          <div className="py-14 text-center text-slate-500">
            <ScanLine className="h-8 w-8 mx-auto mb-3 text-slate-700" />
            Scan a card to start building your queue.
          </div>
        ) : (
          <>
            <div className="divide-y" style={{ borderColor: "var(--pk-border-subtle)" }}>
              {queue.map((q) => (
                <div key={q.key} className="flex items-center gap-3 px-4 py-3" data-testid="scan-queue-row">
                  {q.image_url ? <img src={q.image_url} alt="" className="h-12 w-9 object-contain rounded bg-white/5 shrink-0" /> : null}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-100 truncate">{q.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{q.set_name} · #{q.card_number} · {fmt(q.tcg_price)}</div>
                  </div>
                  <select value={q.condition} onChange={(e) => updateQ(q.key, { condition: e.target.value })} data-testid="scan-queue-condition"
                    className="px-2 py-1.5 rounded-lg border bg-transparent text-slate-100 text-xs outline-none" style={{ borderColor: "var(--pk-border)" }}>
                    {CONDS.map((c) => <option key={c} value={c} className="bg-[#181D2A]">{c}</option>)}
                  </select>
                  <div className="flex items-center gap-1">
                    <button onClick={() => updateQ(q.key, { quantity: Math.max(1, q.quantity - 1) })} data-testid="scan-queue-dec" className="h-7 w-7 rounded-lg border flex items-center justify-center text-slate-300 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}><Minus className="h-3.5 w-3.5" /></button>
                    <span className="w-7 text-center font-mono-num text-sm text-slate-100">{q.quantity}</span>
                    <button onClick={() => updateQ(q.key, { quantity: q.quantity + 1 })} data-testid="scan-queue-inc" className="h-7 w-7 rounded-lg border flex items-center justify-center text-slate-300 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                  <button onClick={() => removeQ(q.key)} data-testid="scan-queue-remove" className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <div className="px-4 py-3 border-t" style={{ borderColor: "var(--pk-border)" }}>
              <button onClick={pushToStaging} disabled={pushing} data-testid="scan-push-staging-button"
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-500 text-[#04120c] font-semibold text-sm hover:bg-emerald-400 disabled:opacity-60">
                {pushing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />}
                Add {queue.length} card(s) to Staging
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
